import type { AxiosError } from "axios";
import axios from "axios";
import { useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled, { useTheme } from "styled-components/macro";
import { AccordionCardWithEditFeatures } from "../../../../components/AccordionCard/AccordionCardwithEditFeatures";
import { AttributeGroupFormGenerator } from "../../../../components/AttributeGroupFormGenerator/AttributeGroupFormGenerator";
import { useAuthContext } from "../../../../components/Auth";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { TooltipPortal } from "../../../../components/TooltipModal/TooltipPortal";
import { endpoints } from "../../../../endpoints";
import { TwoColumGrid } from "../../../../layout/portalPageLayout";
import type { DataMutate, OptionType, UUID } from "../../../../types/types";
import type {
  AttributeGroupSummary,
  AttributeSchema,
  ImageAttributeValue,
  PIMProduct,
  SupportedAssetAccessibility,
} from "../../../../types/types.PIM";
import { useStoreState, useUpdateProductStatus } from "../../../../util/util";
import type { ProductGroupFormValues } from "./product_overview.utils";
import {
  AttributeValueDisplay,
  isExistingAttributeFormValue,
  processAttributeFormValue,
} from "./product_overview.utils";

interface IProductGroupCardProps {
  product: PIMProduct;
  group: AttributeGroupSummary;
  mutateProduct: DataMutate<PIMProduct>;
  replaceProductId: (id: string) => void;
  key: string;
  isPortfolio?: boolean;
}
interface IProductGroupCardDetailsProps {
  group: AttributeGroupSummary;
  isPortfolio?: boolean;
  showMandatoryAsterisk: boolean;
  shouldHideEmptyAttributes: boolean;
}

const EditFormWrapper = styled.div`
  margin-top: 20px;
`;

const InnerTitleText = styled.p`
  color: ${({ theme }) => theme.secondaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  display: inline-block;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  margin: ${({ theme }) => theme.resolve("spacing_xl")} 0
    ${({ theme }) => theme.resolve("spacing_xxs")};
`;

const ProductGroupCardDetails = ({
  group,
  isPortfolio = false,
  showMandatoryAsterisk,
  shouldHideEmptyAttributes,
}: IProductGroupCardDetailsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { roleIsSomeKindOfSeller, roleIsGuest, roleIsSomeKindOfBuyer } =
    useAuthContext();
  const entirelySelectAttributes = group.attributes.every(
    (attr) =>
      (attr.input_type === "single_select" ||
        attr.input_type === "multi_select") &&
      attr.icons_enabled
  );

  const entirelyImageAttributes = group.attributes.every(
    (attr) => attr.input_type === "image"
  );

  const content = group.attributes.reduce<JSX.Element[]>((acc, attribute) => {
    const is_not_accessible = (accessibility?: SupportedAssetAccessibility) => {
      return roleIsGuest
        ? accessibility !== "public"
        : roleIsSomeKindOfBuyer
        ? accessibility === "internal"
        : false;
    };
    if (
      (shouldHideEmptyAttributes &&
        (!attribute.values?.[0]?.value ||
          (attribute.input_type === "image" &&
            is_not_accessible(attribute.values?.[0]?.visibility ?? "public")) ||
          attribute.is_not_applicable ||
          attribute.is_restricted ||
          !attribute.is_visible)) ||
      (!shouldHideEmptyAttributes && !attribute.is_visible)
    ) {
      return acc;
    }

    const attributeContent = (
      <div key={attribute.id}>
        <InnerTitleText
          data-for={attribute.id}
          data-tip={
            roleIsSomeKindOfSeller && attribute.description
              ? attribute.description
              : undefined
          }
        >
          {attribute.display_name
            ? t([attribute.display_name]) +
              (attribute.is_mandatory && !isPortfolio && showMandatoryAsterisk
                ? " *"
                : "")
            : attribute.name +
              (attribute.is_mandatory && !isPortfolio && showMandatoryAsterisk
                ? " *"
                : "")}
        </InnerTitleText>
        <AttributeValueDisplay
          attribute={attribute}
          isPortfolio={isPortfolio}
        />
        {roleIsSomeKindOfSeller && attribute.description?.replace(/\s/g, "") ? (
          <TooltipPortal>
            <ReactTooltip
              id={attribute.id}
              place="top"
              data-html={true}
              effect="solid"
              backgroundColor={theme.tooltipBG}
              multiline={true}
            />
          </TooltipPortal>
        ) : (
          <></>
        )}
      </div>
    );

    acc.push(attributeContent);
    return acc;
  }, []);

  return entirelySelectAttributes || entirelyImageAttributes ? (
    <div>{content}</div>
  ) : (
    <TwoColumGrid>{content}</TwoColumGrid>
  );
};

export const ProductGroupCard = ({
  product,
  group,
  mutateProduct,
  isPortfolio = false,
  replaceProductId,
}: IProductGroupCardProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const { tenant_id } = useStoreState();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { t } = useTranslation();
  const updateProductStatus = useUpdateProductStatus({ product });
  const { hasPermission, roleIsSomeKindOfSeller } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const methodsOfUseForm = useForm();

  // Hide empty attributes for portfolio view or sellers who can't edit
  // Users need BOTH modify_products permission AND is_editable to see blank attributes
  const shouldHideEmptyAttributes =
    isPortfolio ||
    (roleIsSomeKindOfSeller &&
      !(hasPermission("modify_products") && product.is_editable));

  const group_attributes_map = useMemo(() => {
    return group.attributes.reduce((acc, attr) => {
      acc[attr.id] = attr;
      return acc;
    }, {} as Record<string, AttributeSchema>);
  }, [group.attributes]);

  const getGroupPatchData = (formValues: ProductGroupFormValues) => {
    const allKeys = Object.keys(formValues);

    // Get non-empty values and values to delete
    const { keys, keys_to_delete } = allKeys.reduce(
      (acc, key) => {
        const value = formValues[key];
        const isEmpty =
          value === undefined ||
          value === "" ||
          value === " " ||
          value === null;
        const isValid = key !== "display_text" && key !== "";

        if (isValid) {
          if (!isEmpty) {
            acc.keys.push(key);
          } else {
            acc.keys_to_delete.push(key);
          }
        }
        return acc;
      },
      { keys: [] as string[], keys_to_delete: [] as string[] }
    );

    // Get value IDs to delete
    const values_to_delete = keys_to_delete.reduce((acc, attr_id) => {
      const attr = group_attributes_map[attr_id];
      if (
        !attr?.is_editable ||
        attr.is_not_applicable ||
        !attr.values?.length
      ) {
        return acc;
      }

      return [
        ...acc,
        ...attr.values
          .filter(({ value }) => value !== null && value !== "")
          .map(({ id }) => id),
      ];
    }, [] as string[]);

    // Build values array for update
    const valuesArray = keys.reduce((acc, attr_id) => {
      const attribute = group_attributes_map[attr_id];
      const is_editable = attribute?.is_editable ?? false;
      const formValue = formValues[attr_id];

      if (!is_editable || attribute?.is_not_applicable) {
        return acc;
      }

      if (Array.isArray(formValue)) {
        // Add new values that don't exist yet
        const newValues = (formValue as (string | OptionType<string>)[])
          .filter(
            (value) =>
              !attribute.values?.some((val) =>
                isExistingAttributeFormValue(
                  value,
                  val.value,
                  attribute.input_type
                )
              )
          )
          .map((value) => ({
            attribute_id: attribute.id,
            value: ((value as OptionType).value ?? value) as string,
          }));
        acc.push(...newValues);

        // Mark existing values for deletion if they're no longer present
        if (attribute.values?.length) {
          const valuesToDelete = attribute.values
            .filter(
              ({ value }) =>
                !formValue.some((v) =>
                  isExistingAttributeFormValue(v, value, attribute.input_type)
                )
            )
            .map(({ id }) => id);
          values_to_delete.push(...valuesToDelete);
        }
      } else {
        const processed = processAttributeFormValue(
          formValue,
          attribute.input_type
        );
        const existing = attribute.values?.[0]?.value;

        if (
          attribute.input_type === "link" &&
          processed == null &&
          attribute.values?.length
        ) {
          values_to_delete.push(...attribute.values.map(({ id }) => id));
        } else if (
          processed &&
          !isExistingAttributeFormValue(
            processed,
            existing,
            attribute.input_type
          )
        ) {
          acc.push({
            attribute_id: attribute.id,
            value: processed,
          });
        }
      }

      return acc;
    }, [] as Array<{ attribute_id: string; value: string | { display_text: string; url: string } }>);

    return {
      values: valuesArray,
      values_to_delete,
    };
  };

  const updateApplicability = async (
    attributesToMakeApplicable: UUID[],
    attributesToMakeUnapplicable: UUID[]
  ) => {
    await axios.put(
      `/v2/tenants/${tenant_id}/pim/products/${product.id}/groups/${group.id}/applicability`,
      {
        attributes_to_make_applicable: attributesToMakeApplicable,
        attributes_to_make_unapplicable: attributesToMakeUnapplicable,
      }
    );
    await mutateProduct();
  };

  const onSubmit = async (formValues: ProductGroupFormValues) => {
    try {
      setIsLoading(true);
      const { values, values_to_delete } = getGroupPatchData(formValues);
      if (values.length === 0 && values_to_delete.length === 0) {
        notifySuccess(t("No changes to save"));
        setIsEditMode(false);
        return;
      }

      const { data: updatedProduct } = await updateProductStatus();

      const { data } = await axios.patch<PIMProduct>(
        `/v2/tenants/${tenant_id}/pim/products/${updatedProduct.id}/groups/${group.id}`,
        { values, values_to_delete }
      );

      if (
        data.display_status === "staged" ||
        data.display_status === "unpublished_staged"
      ) {
        const product_id =
          data.product_number ?? data.primary_staged_product_id ?? data.id;
        replaceProductId(product_id);
      } else {
        await mutateProduct();
      }
      notifySuccess(t("Your changes have been saved"));
      setIsEditMode(false);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error saving your changes"),
        {
          error,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onCreateOption = async (value: string, attribute: AttributeSchema) => {
    if (!hasPermission("modify_lists")) {
      notifyError(
        t("You don't have permission to modify list {{list_name}}", {
          list_name: attribute.list_name,
        })
      );
      return;
    }
    try {
      const response = await axios.post<{ name: string; id: UUID }>(
        endpoints.v2_tenants_id_or_slug_pim_lists_id_items(
          tenant_id,
          attribute.list_id
        ),
        {
          name: value,
        }
      );
      if (attribute.input_type === "single_select") {
        methodsOfUseForm.setValue(attribute.id, {
          value: response.data.id,
          label: value,
        });
        mutateProduct();
      }
      notifySuccess(
        t("{{value}} was saved to the {{list_name}} list", {
          value: value,
          list_name: attribute.list_name,
        })
      );
    } catch (error) {
      notifyError(
        t("There was an error adding {{value}} to the {{list_name}} list", {
          value: value,
          list_name: attribute.list_name,
        }),
        { error }
      );
    }
  };

  const onCreateImageAttr = async (
    image_attr: ImageAttributeValue | null,
    attribute: AttributeSchema
  ) => {
    methodsOfUseForm.setValue(attribute.id, image_attr);
  };

  return (
    <div className="product-group-card">
      <AccordionCardWithEditFeatures
        title={group.display_name ? t([group.display_name]) : t(group.name)}
        handleEdit={
          !isPortfolio &&
          hasPermission("modify_products") &&
          product.is_editable &&
          product.status !== "archived"
            ? () => setIsEditMode(!isEditMode)
            : undefined
        }
        expanded={true}
        editing={isEditMode}
      >
        {!isEditMode && (
          <ProductGroupCardDetails
            group={group}
            isPortfolio={isPortfolio}
            showMandatoryAsterisk={
              product.product_schema.show_mandatory_completion_score &&
              !isPortfolio
            }
            shouldHideEmptyAttributes={shouldHideEmptyAttributes}
          />
        )}
        {!isPortfolio && isEditMode && product.status !== "archived" && (
          <EditFormWrapper>
            <AttributeGroupFormGenerator
              attributes={(group.attributes as AttributeSchema[]).filter(
                (attr) => attr.is_visible
              )}
              onSubmit={onSubmit}
              onCancel={() => setIsEditMode(false)}
              methodsOfUseForm={methodsOfUseForm}
              onCreateOption={onCreateOption}
              onCreateImageAttribute={onCreateImageAttr}
              isLoading={isLoading}
              updateApplicability={updateApplicability}
              showMandatoryAsterisk={
                product.product_schema.show_mandatory_completion_score
              }
              pimProductType={product.pim_product_type}
            />
          </EditFormWrapper>
        )}
      </AccordionCardWithEditFeatures>
    </div>
  );
};
